/**
 * A commitment's own page — /prioritization/commitments/:commitmentId
 *
 * Drawn in the item page's frame (DetailPageFrame) and written in its parts, so
 * a commitment reads the way an item does:
 *
 *   ‹ Back to commitments  (the list as it was left: its tile, filters, search)
 *   Commitment · Customer · Active · Owner              Delete
 *   Title, and what was promised
 *   Coverage │ Assignments │ Due
 *   Overview · Assignments · Continuity
 *   the tab                                             At a glance
 *
 * Overview holds the linked items (and their coverage) and the conditions;
 * Assignments, who is bound by whose authority (959, 983); Continuity, the
 * ruling and Supersede (983). The right-hand rail edits the fields in place.
 * A link to ?tab=assignments from an ask (981) opens on the assignments.
 */
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { DetailPageFrame, DetailRail, type DetailRailGroup } from '@/components/detail-page/DetailPageFrame';
import {
  DestinationQuestion,
  EditorError,
  InlineChoiceEditor,
  InlineTextEditor,
  OwnedSectionHeading,
} from '@/components/detail-page/detailPageParts';
import { Callout, Modal, TabPanel } from '@/components/ds';
import { ObjectRef } from '@/components/ds/ObjectRef';
import { btnDanger, btnSecondary, status } from '@/components/mono/primitives';
import { SearchableItemPicker } from '@/components/prioritization/SearchableItemPicker';
import ConditionsPanel from '@/components/prioritization/conditions/ConditionsPanel';
import { AssignmentsSection } from '@/components/prioritization/commitments/AssignmentsSection';
import { AuthorityCoverageLine } from '@/components/prioritization/commitments/AuthorityCoverageLine';
import { CommitmentLineage, ContinuitySection } from '@/components/prioritization/commitments/ContinuitySection';
import {
  COMMITMENT_COVERAGE,
  COMMITMENT_PRIORITY_LABELS,
  COMMITMENT_PRIORITY_TONE,
  COMMITMENT_STATUS_LABELS,
  COMMITMENT_STATUS_TONE,
  COMMITMENT_TYPE_LABELS,
  EDITABLE_COMMITMENT_STATUSES,
  coverageCountLine,
  dueLine,
  formatDueDate,
} from '@/components/prioritization/commitments/commitmentDisplay';
import { useConditionsEnabled } from '@/hooks/useConditionsEnabled';
import { listAuthorityCoverage, type AuthorityCoverageRow } from '@/lib/assignmentService';
import { findActivePlanCommitmentsForItems } from '@/lib/conditionObjectsService';
import { formatDate } from '@/i18n/formatters';
import { useCommitmentStore } from '@/stores/prioritization/commitmentStore';
import { useItemsStore } from '@/stores/prioritization/itemsStore';
import { useTenantStore } from '@/stores/prioritization/tenantStore';
import { FollowButton } from '@/components/follow/FollowButton';
import type {
  CommitmentPriority,
  CommitmentType,
  CommitmentWithCoverage,
  UpdateCommitmentInput,
} from '@/types/prioritization/commitment';

type Tab = 'overview' | 'assignments' | 'continuity';
const TABS: Tab[] = ['overview', 'assignments', 'continuity'];
const LIST_PATH = '/prioritization/commitments';

export default function CommitmentDetailPage() {
  const { commitmentId } = useParams<{ commitmentId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const nav = (location.state ?? {}) as { back?: string; note?: string | null; fromAsk?: boolean };
  const backHref = nav.back ?? LIST_PATH;

  const { syncState, fetchFromSupabase, updateCommitment, deleteCommitment, linkItem, unlinkItem, getCommitmentsWithCoverage, commitments } =
    useCommitmentStore();
  const items = useItemsStore((s) => s.items);
  const fetchItems = useItemsStore((s) => s.fetchFromSupabase);

  useEffect(() => {
    void fetchFromSupabase();
    if (items.length === 0) void fetchItems();
    // Once on arrival; the store keeps itself after that.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchFromSupabase, fetchItems]);

  const all: CommitmentWithCoverage[] = useMemo(
    () => getCommitmentsWithCoverage(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [commitments, getCommitmentsWithCoverage, syncState.lastSyncAt],
  );
  const c = all.find((x) => x.id === commitmentId) ?? null;

  // ── Authority coverage (983) for this commitment ────────────────────────────
  const workspaceId = useTenantStore((s) => s.currentWorkspaceId);
  const [authority, setAuthority] = useState<AuthorityCoverageRow | null>(null);
  const refreshAuthority = useCallback(async () => {
    if (!workspaceId || !commitmentId) return;
    try {
      const rows = await listAuthorityCoverage(workspaceId);
      setAuthority(rows.find((r) => r.commitment_id === commitmentId) ?? null);
    } catch {
      // A reading, not a gate: on a failed read the line is not drawn.
      setAuthority(null);
    }
  }, [workspaceId, commitmentId]);
  useEffect(() => {
    void refreshAuthority();
  }, [refreshAuthority, syncState.lastSyncAt]);

  // ── The tab lives in the URL, as on the item page ───────────────────────────
  const tabParam = params.get('tab') as Tab | null;
  const tab: Tab = tabParam && TABS.includes(tabParam) ? tabParam : 'overview';
  const setTab = (t: Tab) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (t === 'overview') next.delete('tab');
        else next.set('tab', t);
        return next;
      },
      { replace: true, state: location.state },
    );

  const [editing, setEditing] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const save = useCallback(
    async (input: UpdateCommitmentInput): Promise<string | null> => {
      if (!c) return null;
      // A refused change is reported by the store (dispatchStoreError), as everywhere else it writes.
      await updateCommitment(c.id, input);
      return null;
    },
    [c, updateCommitment],
  );

  const linkedItems = useMemo(() => {
    const ids = new Set(c?.linkedItemIds ?? []);
    return items.filter((it) => ids.has(it.id));
  }, [items, c?.linkedItemIds]);

  if (!c) {
    const loading = syncState.isLoading || !syncState.lastSyncAt;
    return (
      <div className="mx-auto max-w-[1200px] px-4 py-6 md:px-6">
        {loading ? (
          <div className="flex items-center gap-2 py-24 text-sm text-[var(--text-ter)]">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading the commitment…
          </div>
        ) : (
          <Callout tone="mustard" label="Not found">
            No commitment with this link is in this workspace. It may have been deleted, or it belongs to another workspace.{' '}
            <Link to={LIST_PATH} className="underline">
              <ArrowLeft className="inline h-3.5 w-3.5" /> Back to commitments
            </Link>
          </Callout>
        )}
      </div>
    );
  }

  const superseded = c.status === 'superseded';
  const coverage = COMMITMENT_COVERAGE[c.coverage];
  const binding = authority?.binding_count ?? null;

  const choiceRow = <V extends string>(
    key: string,
    k: string,
    v: ReactNode,
    current: V,
    labels: Record<V, string>,
    values: V[],
    field: keyof UpdateCommitmentInput,
  ) => {
    const open = editing === key;
    return {
      k,
      v,
      onClick: () => setEditing(open ? null : key),
      open,
      editor: open ? (
        <InlineChoiceEditor
          stacked
          allowClear={false}
          label={k}
          initial={current}
          options={values.map((x) => ({ value: x, label: labels[x] }))}
          onSave={(value) => save({ [field]: value } as UpdateCommitmentInput)}
          close={() => setEditing(null)}
        />
      ) : undefined,
    };
  };
  const textRow = (key: string, k: string, v: ReactNode, initial: string, field: keyof UpdateCommitmentInput, inputType?: 'date') => {
    const open = editing === key;
    return {
      k,
      v,
      onClick: () => setEditing(open ? null : key),
      open,
      editor: open ? (
        <InlineTextEditor
          label={k}
          initial={initial}
          inputType={inputType}
          onSave={(value) => save({ [field]: value || null } as UpdateCommitmentInput)}
          close={() => setEditing(null)}
        />
      ) : undefined,
    };
  };

  const rail: DetailRailGroup[] = [
    {
      h: 'At a glance',
      rows: [
        choiceRow<CommitmentType>('type', 'Made to', COMMITMENT_TYPE_LABELS[c.commitmentType], c.commitmentType, COMMITMENT_TYPE_LABELS, Object.keys(COMMITMENT_TYPE_LABELS) as CommitmentType[], 'commitmentType'),
        textRow('counterparty', 'Counterparty', c.counterparty || <NotSet />, c.counterparty ?? '', 'counterparty'),
        // A superseded commitment stays superseded (983): its status is read, not edited.
        superseded
          ? { k: 'Status', v: COMMITMENT_STATUS_LABELS[c.status] }
          : choiceRow('status', 'Status', COMMITMENT_STATUS_LABELS[c.status], c.status, COMMITMENT_STATUS_LABELS, EDITABLE_COMMITMENT_STATUSES, 'status'),
        choiceRow<CommitmentPriority>('priority', 'Priority', COMMITMENT_PRIORITY_LABELS[c.priority], c.priority, COMMITMENT_PRIORITY_LABELS, Object.keys(COMMITMENT_PRIORITY_LABELS) as CommitmentPriority[], 'priority'),
        textRow('due', 'Due', c.dueDate ? formatDueDate(c.dueDate) : <NotSet />, c.dueDate ?? '', 'dueDate', 'date'),
        textRow('owner', 'Owner', c.ownerName || <NotSet />, c.ownerName ?? '', 'ownerName'),
      ],
    },
    {
      h: 'Record',
      rows: [
        { k: 'Created', v: formatDate(c.createdAt, { month: 'short', day: 'numeric', year: 'numeric' }) },
        { k: 'Updated', v: formatDate(c.updatedAt, { month: 'short', day: 'numeric', year: 'numeric' }) },
      ],
    },
  ];

  const titleOpen = editing === 'title';
  const descriptionOpen = editing === 'description';

  return (
    <div className="mx-auto max-w-[1200px] px-4 pb-10 md:px-6">
      {nav.note && (
        <Callout tone="mustard" label="Note" className="mt-6">
          {nav.note}
        </Callout>
      )}
      <DetailPageFrame<Tab>
        back={{ label: 'Back to commitments', href: backHref }}
        identity={
          <>
            <ObjectRef kind="commitment" name={COMMITMENT_TYPE_LABELS[c.commitmentType]} />
            <span {...status(COMMITMENT_STATUS_TONE[c.status])}>{COMMITMENT_STATUS_LABELS[c.status]}</span>
            <span {...status(COMMITMENT_PRIORITY_TONE[c.priority])}>{COMMITMENT_PRIORITY_LABELS[c.priority]}</span>
            <span className="text-[13.5px] text-[var(--text-dim)]">
              Owner{' '}
              {c.ownerName ? <strong className="text-[var(--text-pri)]">{c.ownerName}</strong> : <strong className="text-[var(--mustard)]">not set</strong>}
            </span>
          </>
        }
        actions={
          <>
            <FollowButton type="commitment" id={c.id} {...btnSecondary()} />
            <button {...btnSecondary()} onClick={() => setConfirmDelete(true)}>
              Delete
            </button>
          </>
        }
        headline={
          <div className="mt-4">
            {titleOpen ? (
              <InlineTextEditor label="Title" initial={c.title} onSave={(v) => (v ? save({ title: v }) : Promise.resolve('A commitment needs a title.'))} close={() => setEditing(null)} />
            ) : (
              <h2 className="text-[26px] font-semibold leading-tight" style={{ color: 'var(--text-pri)' }}>
                {c.title}{' '}
                <button {...btnSecondary('!h-7 align-middle')} onClick={() => setEditing('title')}>
                  Edit
                </button>
              </h2>
            )}
            {c.counterparty && <p className="mt-1 text-[var(--text-ter)]">To {c.counterparty}</p>}
            {descriptionOpen ? (
              <div className="mt-2 max-w-[84ch]">
                <InlineTextEditor multiline label="What was promised" initial={c.description ?? ''} onSave={(v) => save({ description: v })} close={() => setEditing(null)} />
              </div>
            ) : (
              <p className="mt-2 max-w-[84ch] whitespace-pre-wrap text-[var(--text-sec)]">
                {c.description || <span className="text-[var(--mustard)]">No description yet.</span>}{' '}
                <button type="button" className="font-mono text-[12px] uppercase tracking-[0.07em] text-[var(--text-ter)] underline underline-offset-[3px]" onClick={() => setEditing('description')}>
                  {c.description ? 'Edit' : 'Add'}
                </button>
              </p>
            )}
          </div>
        }
        standing={[
          { label: 'Coverage', value: <span {...status(coverage.tone)}>{coverage.label}</span>, note: coverageCountLine(c) ?? coverage.detail },
          { label: 'Assignments', value: binding ?? '—', note: binding == null ? 'Not read' : 'binding' },
          { label: 'Due', value: formatDueDate(c.dueDate), note: dueLine(c.dueDate) },
        ]}
        banner={
          <div className="mt-3">
            <CommitmentLineage commitment={c} allCommitments={all} onOpen={(id) => navigate(`${LIST_PATH}/${id}`, { state: { back: backHref } })} />
          </div>
        }
        tabs={[
          { id: 'overview', label: 'Overview' },
          { id: 'assignments', label: 'Assignments', count: binding || undefined },
          { id: 'continuity', label: 'Continuity' },
        ]}
        activeTab={tab}
        onTabChange={setTab}
        tabsLabel="Commitment destinations"
        rail={<DetailRail groups={rail} />}
      >
        <TabPanel id="overview" active={tab}>
          <DestinationQuestion>What was promised, and is the work behind it on the plan?</DestinationQuestion>
          <OwnedSectionHeading title={`Linked items · ${linkedItems.length}`} owned="the work that keeps this promise" />
          {linkedItems.length === 0 && <p className="py-2.5 text-[var(--text-dim)]">No items linked yet. Link the items whose work keeps this promise.</p>}
          {linkedItems.map((it) => {
            const covered = c.coveredItemIds.includes(it.id);
            return (
              <div key={it.id} className="grid grid-cols-[minmax(0,1fr)_auto_auto] items-baseline gap-x-4 border-b py-2.5" style={{ borderColor: 'var(--rule)' }}>
                <Link to={`/prioritization/items/${it.id}`} className="truncate text-[var(--text-pri)] underline decoration-[var(--rule-bright)] underline-offset-[3px]">
                  {it.title ?? '(untitled)'}
                </Link>
                <span {...status(covered ? 'green' : 'mustard')}>{covered ? 'In an active cycle' : 'In no active cycle'}</span>
                {!superseded && (
                  <button type="button" className="font-mono text-[12px] uppercase tracking-[0.07em] text-[var(--text-ter)] underline underline-offset-[3px] hover:text-[var(--coral)]" onClick={() => void unlinkItem(c.id, it.id)}>
                    Unlink
                  </button>
                )}
              </div>
            );
          })}
          {!superseded && (
            <div className="mt-3 max-w-md">
              <SearchableItemPicker
                label="Link an item"
                placeholder="Search items to link…"
                value={undefined}
                excludeIds={c.linkedItemIds}
                allowEmpty={false}
                onChange={(id) => {
                  if (id) void linkItem(c.id, id);
                }}
              />
            </div>
          )}
          <CommitmentConditions commitment={c} linkedItems={linkedItems} />
        </TabPanel>

        <TabPanel id="assignments" active={tab}>
          <DestinationQuestion>Who is bound to do what, and by whose authority?</DestinationQuestion>
          <AuthorityCoverageLine row={authority} scope="commitment" className="mt-3" />
          <div className="mt-3">
            <AssignmentsSection
              home={{ type: 'commitment', id: c.id }}
              linkedItems={linkedItems}
              readOnly={superseded}
              onChanged={() => void refreshAuthority()}
              scrollIntoViewOnLoad={Boolean(nav.fromAsk)}
            />
          </div>
        </TabPanel>

        <TabPanel id="continuity" active={tab}>
          <DestinationQuestion>Is this still the same promise?</DestinationQuestion>
          <div className="mt-3">
            <ContinuitySection
              commitment={c}
              allCommitments={all}
              linkedItems={linkedItems}
              onChanged={async (next) => {
                await fetchFromSupabase();
                await refreshAuthority();
                if (next?.select) navigate(`${LIST_PATH}/${next.select}`, { state: { back: backHref } });
              }}
            />
          </div>
        </TabPanel>
      </DetailPageFrame>

      <Modal
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title="Delete this commitment?"
        description={`“${c.title}” and its item links are removed. This cannot be undone.`}
        footer={
          <>
            <button {...btnSecondary()} onClick={() => setConfirmDelete(false)}>
              Cancel
            </button>
            <button
              {...btnDanger()}
              onClick={async () => {
                await deleteCommitment(c.id);
                setConfirmDelete(false);
                navigate(backHref);
              }}
            >
              Delete
            </button>
          </>
        }
      />
    </div>
  );
}

function NotSet() {
  return <span className="text-[var(--mustard)]">Not set</span>;
}

// ── Conditions (break 2 of the decision→…→reprioritization chain) ────────────
//
// ConditionsPanel's `{kind:'commitment'}` subject is keyed on (plan_cycle_id,
// item_id) — a plan commitment, not a Commitment Registry row (see
// conditionObjectsService.ts on findActivePlanCommitmentsForItems). A registry
// commitment carries conditions only through a linked item that is itself part
// of an active plan cycle; this section resolves that per linked item and
// offers the panel only where it is real, with a plain reason otherwise.

function CommitmentConditions({
  commitment,
  linkedItems,
}: {
  commitment: CommitmentWithCoverage;
  linkedItems: { id: string; title?: string | null }[];
}) {
  const conditionsEnabled = useConditionsEnabled();
  const [eligibility, setEligibility] = useState<{ itemId: string; planCycleId: string; planCycleName: string }[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  // A plain string, not the array: the effect keys off a primitive cheap to recompute.
  const linkedItemIds = linkedItems.map((i) => i.id).sort().join(',');

  useEffect(() => {
    if (!conditionsEnabled) return;
    const ids = linkedItemIds ? linkedItemIds.split(',') : [];
    if (ids.length === 0) {
      setEligibility([]);
      return;
    }
    let cancelled = false;
    void findActivePlanCommitmentsForItems(ids).then((result) => {
      if (cancelled) return;
      setEligibility(result.rows);
      setLoadError(result.error ?? null);
    });
    return () => {
      cancelled = true;
    };
  }, [conditionsEnabled, linkedItemIds]);

  if (!conditionsEnabled) return null;

  const itemTitle = (itemId: string) => linkedItems.find((i) => i.id === itemId)?.title ?? itemId;

  return (
    <>
      <OwnedSectionHeading title="Conditions" owned="held through a linked item on an active plan" />
      {linkedItems.length === 0 ? (
        <p className="py-2.5 text-[var(--text-dim)]">Conditions can be recorded once this commitment has a linked item that is part of an active plan.</p>
      ) : eligibility === null ? (
        <p className="py-2.5 text-[var(--text-dim)]">Checking which linked items are on an active plan…</p>
      ) : loadError ? (
        <EditorError error={loadError} />
      ) : eligibility.length === 0 ? (
        <p className="py-2.5 text-[var(--text-dim)]">
          None of the linked items is part of an active plan cycle. Conditions attach to a (plan cycle, item) pair: place an item in an active plan to record one.
        </p>
      ) : (
        <div className="mt-3 space-y-4">
          {eligibility.map((e) => (
            <ConditionsPanel
              key={`${e.planCycleId}:${e.itemId}`}
              subject={{ kind: 'commitment', planCycleId: e.planCycleId, itemId: e.itemId }}
              subjectLabel={`${commitment.title} — ${itemTitle(e.itemId)} (${e.planCycleName})`}
              workspaceId={commitment.workspaceId}
            />
          ))}
        </div>
      )}
    </>
  );
}
